// components/company/CompanyHeader.jsx
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";

// Re-use the same readable formatting as StatusHistory
function formatDate(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
}

export const CompanyHeader = ({ company, onStatusChange, onApprove, onResign, onDelete, onRestore, onPurge, loading = false, statusMeta }) => {
  const location = useLocation();
  const [isChangingStatus, setIsChangingStatus] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [effectiveDate, setEffectiveDate] = useState("");
  const [reason, setReason] = useState("");

  const backTo = location.state?.from || "/overview";

  const startStatusEdit = (status) => {
    setPendingStatus(status);
    const today = new Date().toISOString().slice(0, 10);
    setEffectiveDate(today);
    setReason("");
  };

  const resetStatusEdit = () => {
    setPendingStatus(null);
    setEffectiveDate("");
    setReason("");
  };

  const performStatusChange = async (payload) => {
    setIsChangingStatus(true);
    try {
      await onStatusChange(payload);
    } finally {
      setIsChangingStatus(false);
      resetStatusEdit();
    }
  };

  const handleStatusSelectChange = async (e) => {
    const newStatus = e.target.value;
    if (!company || newStatus === company.status) return;

    if (newStatus === "liquidated" || newStatus === "resigned") {
      // Inline expansion: ask for date + reason before calling API
      startStatusEdit(newStatus);
      return;
    }

    // Simple statuses: change immediately
    await performStatusChange({ status: newStatus });
  };

  const handleStatusDetailsSave = async () => {
    if (!pendingStatus) return;

    const payload = {
      status: pendingStatus,
      effectiveDate: effectiveDate || undefined,
      reason: reason?.trim() || undefined,
    };

    await performStatusChange(payload);
  };

  const handleStatusDetailsCancel = () => {
    resetStatusEdit();
  };

  const handleResign = () => {
    if (!company) return;
    if (!confirm("Mark this company as resigned?")) return;
    onResign();
  };

  const handleDelete = () => {
    if (!confirm("Move this company to Trash (soft delete)?")) return;
    const reason = prompt("Optional reason for deletion (will be logged):", "") || "";
    onDelete(reason);
  };

  const handlePurge = () => {
    const confirmation = prompt("Type PURGE to permanently delete this company and all related data.");
    if (confirmation === "PURGE") {
      onPurge();
    }
  };

  const title = company ? company.ic_name || company.name || `Company ${company.id}` : "Loading...";

  const currentStatus = company?.status || "pending_gov_approval";
  const selectValue = pendingStatus || currentStatus;

  const renderStatusMeta = () => {
    if (!statusMeta) return null;

    const rawDate = statusMeta.effective_date || statusMeta.created_at;
    const pretty = formatDate(rawDate);
    if (!pretty) return null;

    return (
      <span className="status-meta">
        &nbsp;•&nbsp;<span>as of {pretty}</span>
      </span>
    );
  };

  return (
    <div className="page-top">
      <Link to={backTo} className="back-link" aria-label="Back to database overview">
        <span aria-hidden="true">←</span> Back
      </Link>

      <h1 className="page-title">{title}</h1>

      {company && (
        <>
          <div className="status-badge-wrapper">
            <div className={`badge status-${currentStatus}`}>{currentStatus}</div>
            {renderStatusMeta()}
          </div>

          <select
            value={selectValue}
            onChange={handleStatusSelectChange}
            disabled={isChangingStatus || loading}
            style={{ marginLeft: 8 }}
            title="Change status"
            aria-label="Change company status">
            <option value="pending_gov_approval">Pending</option>
            <option value="active">Active</option>
            <option value="liquidated">Liquidated</option>
            <option value="rejected">Rejected</option>
            <option value="resigned">Resigned</option>
            <option value="deleted">Deleted</option>
          </select>

          {pendingStatus && (
            <div className="status-details-inline" style={{ marginLeft: 8, display: "inline-block" }}>
              <label style={{ marginRight: 8 }}>
                Date:
                <input type="date" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} disabled={isChangingStatus || loading} style={{ marginLeft: 4 }} />
              </label>
              <label style={{ marginRight: 8 }}>
                Reason (optional):
                <input type="text" value={reason} onChange={(e) => setReason(e.target.value)} disabled={isChangingStatus || loading} style={{ marginLeft: 4, minWidth: 260 }} />
              </label>
              <button type="button" onClick={handleStatusDetailsSave} disabled={isChangingStatus || loading} style={{ marginRight: 4 }}>
                Save
              </button>
              <button type="button" className="secondary" onClick={handleStatusDetailsCancel} disabled={isChangingStatus || loading}>
                Cancel
              </button>
            </div>
          )}

          {company.status === "pending_gov_approval" && (
            <button onClick={onApprove} disabled={loading} style={{ marginLeft: 8 }} type="button">
              Approve
            </button>
          )}

          <button onClick={() => startStatusEdit("resigned")} className="secondary" disabled={loading} style={{ marginLeft: 8 }} type="button">
            Mark Resigned
          </button>

          <button type="button" className="danger" disabled={loading} style={{ marginLeft: 8 }} onClick={handleDelete}>
            Delete
          </button>

          {company.status === "deleted" && (
            <>
              <button type="button" className="secondary" disabled={loading} style={{ marginLeft: 8 }} onClick={onRestore}>
                Restore
              </button>

              <button type="button" className="danger" disabled={loading} style={{ marginLeft: 8 }} onClick={handlePurge}>
                Purge
              </button>
            </>
          )}
        </>
      )}

      <div className="spacer" />
    </div>
  );
};
